<script lang="ts" setup>
import { computed, ref } from 'vue';

import { useClickOutside } from '~/composables/useClickOutside';
import { TagEntity } from '~/entities/contacts';

interface ITag {
  id: string;
  name: string;
}

const props = defineProps<{ tags: { tag: TagEntity; id: string }[] }>();
const emit = defineEmits<{
  (event: 'onDelete', tagId: string): void;
}>();
const showTags = ref(false);
const tagToggler = ref<HTMLDivElement>();
const clickerRef = ref<HTMLDivElement>();

const tagFirst = computed<ITag | null>(() => {
  const first = props.tags[0];

  return first ? { id: first.id, name: first.tag.name } : null;
});
const withoutFirst = computed<ITag[]>(() =>
  props.tags.slice(1).map(({ tag, id }) => ({ id, name: tag.name })),
);

useClickOutside(tagToggler, (e) => {
  if (!e.composedPath().includes(clickerRef.value!)) {
    showTags.value = false;
  }
});

const onClick = async (item: ITag) => {
  emit('onDelete', item.id);
};
</script>

<template>
  <div class="tag-container" ref="tagToggler">
    <BaseTag v-if="tagFirst" :color="`fog`" :text="tagFirst.name" closable @close="onClick(tagFirst)" />
    <template v-if="props.tags.length > 1">
      <div @click="showTags = !showTags" ref="clickerRef">
        <template v-if="!showTags">
          <BaseTag :color="`fog`" :text="`+ ${props.tags.length - 1}`" />
        </template>
        <template v-else>
          <BaseTag :color="`fog`">
            <template #right>
              <IconCrossSm style="margin-left: 0px" />
            </template>
          </BaseTag>
        </template>
      </div>

      <div class="tooltip-wrapper">
        <div v-if="showTags" class="tooltip">
          <div class="tag-container">
            <BaseTag v-for="item of withoutFirst" :color="`fog`" :text="item.name" closable @close="onClick(item)" />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.tag-container {
  display: flex;
  gap: 6px;
  max-width: 376px;
  width: fit-content;

  .tag {
    width: fit-content;
    padding: 6px 12px;
    border-radius: 40px;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    background: #f5f5f5;
    color: #212121;
    white-space: nowrap;

    .icon-container {
      padding: 0px 4px;

      :deep(svg) {
        filter: invert(46%) sepia(0%) saturate(17%) hue-rotate(158deg) brightness(99%) contrast(85%);
      }
    }
  }

  .tag-number {
    cursor: pointer;
  }

  .tooltip-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    .tooltip {
      box-sizing: content-box;
      position: absolute;
      background: #fff;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.14), 0 3px 4px rgba(0, 0, 0, 0.12), 0 1px 5px rgba(0, 0, 0, 0.2);
      border-radius: 4px;
      top: 38px;
      right: 3px;
      padding: 12px;
      z-index: $zindex-tooltip;
      width: max-content;
      max-width: 376px;
      max-height: 216px;
      overflow-y: auto;

      .tag-container {
        flex-wrap: wrap;
        text-overflow: ellipsis;

        .tag {
          &:hover {
            white-space: normal;
          }
        }
      }
    }
  }
}
</style>
